import React from 'react';
import type { CFScene, CFSceneSwitchType, GeneralID, Hotspot } from '@types';
import addComponent from '@utils/addComponent';
import { Collapse } from '@components/Collapse';
import { Dropdown } from '@components/Dropdown';
import { Validator } from '@components/Validator';
import { $sceneMap, $tourData } from '@stores/dataStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import Button from '@components/Button';
import { $loadedId } from '@stores/loadedStore';
import { showConfirmModal } from '@components/ConfirmModal';
import { getHotspotTitleId } from '@modules/hotspot';
import { useCustomFeature } from '@modules/panel/CustomFeatureContext';
import SceneSelector from '@modules/panel/components/SceneSelector';
import SwitchSettingModal from '@modules/panel/components/SwitchSettingModal';
import { switchTypeItems } from '@modules/panel/constants';

type Props = {
  hotspot?: Hotspot;
  onTargetNameChange?: (name: string) => void;
};

/**
 * 场景切换模块, 用于热点和图层
 */
function AdvSceneSetting({ hotspot, onTargetNameChange }: Props) {
  const { data, setData } = useCustomFeature<CFScene>();
  const handleSceneChange = async (id: GeneralID) => {
    const scene = $sceneMap.getState().get(id);
    if (!scene) return;
    const typeChanged =
      data.sceneId &&
      $sceneMap.getState().get(data.sceneId)?.type !== scene.type;
    if (typeChanged) {
      const { confirmed } = await showConfirmModal({
        title: '确认操作',
        content: (
          <div className="text-center">
            当前目标场景与原目标场景的场景类型不同，修改后“切换动画高级设置”中相关设置将重置为默认值，确认修改目标场景？
          </div>
        ),
      });
      if (!confirmed) return;
    }
    setData((draft) => {
      if (typeChanged) {
        draft.exitTargetView = undefined;
        draft.enterInitView = undefined;
        draft.enterTargetView = undefined;
      }
      draft.sceneId = id;
    });

    onTargetNameChange?.(scene.name);

    if (hotspot) {
      const titleId = getHotspotTitleId(hotspot.id, hotspot.icon.type);
      MainPlayer.get()?.updateDivHotspot({
        ...hotspot,
        id: titleId!,
        title: scene.name,
      });
    }
  };

  const handleTypeChange = (typeId: CFSceneSwitchType) => {
    setData((draft) => {
      draft.animation = typeId;
    });
  };

  const handleSwitchSettingClick = () => {
    const tourData = $tourData.getState();
    const map = $sceneMap.getState();
    const sceneId = $loadedId.getState();
    addComponent((remove) => {
      return (
        <SwitchSettingModal
          exitScene={map.get(sceneId!)!}
          enterScene={map.get(data.sceneId)!}
          dragMode={tourData.flag?.dragMode}
          hotspot={hotspot}
          data={data}
          onConfirm={(settings) => {
            setData((draft) => {
              draft.exitTargetView = settings.exitTargetView;
              draft.enterInitView = settings.enterInitView;
              draft.enterTargetView = settings.enterTargetView;
              draft.animation = settings.animation;
            });
          }}
          onRequestClose={remove}
        />
      );
    });
  };

  return (
    <Collapse title="场景切换设置">
      <Validator
        value={data.sceneId}
        validFn={(value) => !!value}
        warningMessage="请选择目标场景"
      >
        {({ value, warning }) => (
          <SceneSelector
            sceneId={value}
            warning={warning}
            onChange={handleSceneChange}
          />
        )}
      </Validator>
      <div className="mt-20 mb-10">切换动画设置</div>
      <Dropdown
        items={switchTypeItems}
        selected={data.animation ?? 0}
        onChange={handleTypeChange}
      />
      <Button
        disabled={!data.sceneId}
        className="mt-20 w-full"
        onClick={handleSwitchSettingClick}
      >
        切换动画高级设置
      </Button>
    </Collapse>
  );
}

export default AdvSceneSetting;
